// @ts-nocheck
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchUserAchievements, fetchModuleContributions, getProfile } from "@/lib/api/users";
import { STUDENT_QUERY_OPTIONS } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ContributionGraph } from "@/components/contribution-graph";
import { SectionLoader } from "@/components/loading";
import { Award, Star, Zap, Target, Trophy, Flame, CalendarDays } from "lucide-react";

export const Route = createFileRoute("/_authenticated/achievements")({
  component: AchievementsPage,
});

function AchievementsPage() {
  const { user } = useAuth();

  const { data: achievements = [], isLoading } = useQuery({
    queryKey: ["user-achievements", user?.id],
    queryFn: () => fetchUserAchievements(user!.id),
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  const { data: profile } = useQuery({
    queryKey: ["user_profile", user?.id],
    queryFn: () => getProfile(user?.id ?? ""),
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  const { data: contributions } = useQuery({
    queryKey: ["module-contributions", user?.id],
    queryFn: () => fetchModuleContributions(user!.id),
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  const totalPoints = achievements.reduce((acc, a) => acc + (a.points || 0), 0);
  const rank =
    totalPoints > 1000 ? "Master Student" : totalPoints > 500 ? "Expert Student" : "Rising Star";

  const badges = [
    {
      name: "First Steps",
      desc: "Completed your first training module",
      icon: <Zap className="h-5 w-5" />,
      earned: achievements.some((a) => a.achievement_type === "module_completed"),
    },
    {
      name: "Perfect Score",
      desc: "Scored 100% on any exam",
      icon: <Star className="h-5 w-5" />,
      earned: achievements.some((a) => a.achievement_type === "exam_passed_100"),
    },
    {
      name: "Course Master",
      desc: "Completed a full training course",
      icon: <Trophy className="h-5 w-5" />,
      earned: achievements.some((a) => a.achievement_type === "course_completed"),
    },
    {
      name: "Certified",
      desc: "Received official training approval",
      icon: <Award className="h-5 w-5" />,
      earned: achievements.some((a) => a.achievement_type === "student_approved"),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Premium Rank Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/95 to-accent/90 text-white rounded-3xl p-6 md:p-8 shadow-lg animate-in fade-in slide-in-from-top-4 duration-500">
        {/* Decorative floating icon */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 opacity-10 pointer-events-none">
          <Trophy
            className="w-64 h-64 text-white animate-pulse"
            style={{ animationDuration: "10s" }}
          />
        </div>

        <div className="relative z-10">
          <p className="text-[10px] text-accent font-bold uppercase tracking-wider">
            Student Milestone
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Trophy
              className="h-7 w-7 text-accent animate-bounce"
              style={{ animationDuration: "3s" }}
            />
            <h1 className="text-3xl font-extrabold tracking-tight">Achievements & Rank</h1>
          </div>
          <p className="mt-2 text-primary-foreground/90 text-sm md:text-base max-w-xl leading-relaxed">
            Track your milestones, view your progress badges, and see your current ranks.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Total Points Card */}
        <Card className="relative overflow-hidden border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-75">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Total Points
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-extrabold text-primary">{totalPoints}</div>
            <p className="text-xs text-muted-foreground mt-1.5 font-medium">
              Keep learning to earn more!
            </p>
          </CardContent>
        </Card>

        {/* Current Rank Card */}
        <Card className="border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Current Rank
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold flex items-center gap-2 text-foreground">
              <Target className="h-5 w-5 text-secondary animate-pulse" />
              {rank}
            </div>
            <Progress value={(totalPoints % 500) / 5} className="h-1.5 mt-3.5 bg-muted" />
            <p className="text-[10px] text-muted-foreground mt-1.5 font-medium">
              Next rank at {Math.ceil((totalPoints + 1) / 500) * 500} pts
            </p>
          </CardContent>
        </Card>

        {/* Streak Card */}
        <Card className="border shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Streak
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold flex items-center gap-2 text-foreground">
              <Flame className="h-5 w-5 text-orange-500 animate-pulse" />
              {profile?.login_streak ?? 0} Days
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 font-medium">
              Log in daily to build your streak.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Module Contributions Heatmap */}
      <Card className="border shadow-sm hover:shadow-md transition-shadow duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
        <CardHeader>
          <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            Module Activity
          </CardTitle>
          <CardDescription className="text-xs">
            Your day-by-day learning activity — modules completed and quizzes answered.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ContributionGraph data={contributions} />
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Badges Card */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-foreground">Badges</CardTitle>
            <CardDescription className="text-xs">
              Official milestones you've achieved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {badges.map((badge, index) => (
                <div
                  key={badge.name}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all duration-300 hover:shadow-md ${
                    badge.earned
                      ? "bg-accent/5 border-accent/20 hover:bg-accent/10 hover:border-accent/30"
                      : "opacity-40 grayscale hover:opacity-50"
                  } animate-in zoom-in-95 duration-500`}
                  style={{ animationDelay: `${index * 50 + 250}ms`, animationFillMode: "both" }}
                >
                  <div
                    className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 ${
                      badge.earned
                        ? "bg-accent/20 text-accent scale-100 group-hover:scale-110 font-bold"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {badge.icon}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground truncate">{badge.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                      {badge.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activity Feed Card */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-foreground">Activity Feed</CardTitle>
            <CardDescription className="text-xs">Your recent accomplishments.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3.5">
              {isLoading ? (
                <SectionLoader label="Loading activity…" />
              ) : achievements.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-10">
                  No recent activity found.
                </p>
              ) : (
                achievements.map((a, index) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-3 text-sm p-2 rounded-xl hover:bg-muted/40 transition-colors duration-200 animate-in fade-in"
                    style={{ animationDelay: `${index * 30 + 350}ms`, animationFillMode: "both" }}
                  >
                    <div className="h-2 w-2 rounded-full bg-primary shrink-0 animate-pulse" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">{a.achievement_name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(a.awarded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-bold text-xs"
                    >
                      +{a.points}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
